import CircleIcon from "@mui/icons-material/Circle";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { SimpleTable } from "../../../../common/components/functional/simpleTable/SimpleTable";
import { SimpleTableHeader } from "../../../../common/components/functional/simpleTable/SimpleTableHeader";
import { RowField } from "../../payslips/PayslipsTable";
import { EmployerHealthInsuranceReduction } from "../types";
import theme from "../../../../theme";
import { TableRowItem } from "./TableRowItem";
import { format } from "date-fns";
import {
  dateTimeFormat,
  shortMonthYearFormat,
} from "../../../../common/helpers/formatDateTime";
import { TablePaginationProps } from "../../../../common/components/functional/pagination/types";
import { SalariesHeader } from "../../SalariesHeader";
import { ScrollContainer } from "../../styled";

interface Props {
  isLoading: boolean;
  items: EmployerHealthInsuranceReduction[];
  editItem: (id: string) => void;
  deleteItem: (id: string) => void;
  pagination?: TablePaginationProps;
}

export const EmployerHealthInsuranceReductionsTable = ({
  isLoading,
  items,
  editItem,
  deleteItem,
  pagination,
}: Props) => {
  const rowFields: RowField[] = [
    { name: "employer", width: "250px" },
    { name: "costCenter", width: "200px" },
    { name: "customerOrg", width: "250px" },
    { name: "identifier", width: "180px" },
    { name: "time", width: "120px" },
    { name: "amount", width: "120px" },
    { name: "statusChanged", width: "180px" },
    { name: "separateReportStatus", width: "60px" },
    { name: "accountingStatus", width: "60px" },
  ];

  const rows = items.map((item) => {
    return {
      id: item.id,
      fields: [
        {
          name: "employer",
          value: item.employerName,
        },
        { name: "costCenter", value: item.employerCostCenterName },
        {
          name: "customerOrg",
          value: item.customerOrganizationName || "",
        },
        {
          name: "identifier",
          value: item.identifier || "",
        },
        {
          name: "time",
          value: format(new Date(item.yearMonth), shortMonthYearFormat),
        },
        {
          name: "amount",
          value: item.amount,
        },
        {
          name: "statusChanged",
          value: item.lastModified
            ? format(new Date(item.lastModified), dateTimeFormat)
            : format(new Date(), dateTimeFormat),
        },
        {
          name: "separateReportStatus",
          value: "",
          type: "icon",
          component: (
            <CircleIcon
              sx={{
                color: !!item.payerSummaryReport
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                width: 15,
                height: 15,
                marginLeft: "20px",
              }}
            />
          ),
        },
        {
          name: "accountingStatus",
          value: "",
          type: "icon",
          component: (
            <CircleIcon
              sx={{
                color: !!item.accounting
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                width: 15,
                height: 15,
                marginLeft: "20px",
              }}
            />
          ),
        },
      ],
    };
  });

  return (
    <ScrollContainer>
      {isLoading ? <LoadingSpinner /> : null}
      <SalariesHeader pagination={pagination} isLoading={isLoading} />
      <SimpleTable
        header={
          <SimpleTableHeader fields={rowFields} translationNs="payslips" />
        }
        items={rows.map((row, index) => {
          const canModify = !items.find((item) => item.id === row.id)
            ?.payerSummaryReport;
          return (
            <TableRowItem
              key={index}
              row={row.fields}
              rowId={row.id}
              canModify={canModify}
              onDeleteRow={deleteItem}
              onEditRow={editItem}
            />
          );
        })}
      />
    </ScrollContainer>
  );
};
